import { useState, useCallback, useEffect } from 'react';

import { debounce } from '@global/utils/debounce';

import { getSuggestions } from './request';
import { SuggestAddress } from './types';

const MIN_ZIP_CODE_LENGTH = 5;
const TYPING_DELAY = 300;
const AUTOCOMPLETE_VALID_COUNTRIES = ['US'];

const cash = new Map<string, SuggestAddress[]>();

type UseAutocompleteProps = {
  onChangeSuggestions: (suggestions: SuggestAddress[]) => void;
};

export const useAutocomplete = (props: UseAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<SuggestAddress[]>([]);

  const updateSuggestions = useCallback(
    async (zipCode: string, countryCode: string) => {
      // try to get cashed suggestions
      const cashKey = countryCode + zipCode;
      const cashedSuggestions = cash.get(cashKey);
      if (cashedSuggestions) {
        props.onChangeSuggestions(cashedSuggestions);
        return;
      }

      // request suggestions
      const suggestions = await getSuggestions({ zipCode, countryCode });
      setSuggestions(suggestions);

      // Update cash
      cash.set(cashKey, suggestions);
    },
    []
  );

  const debouncedUpdateSuggestions = useCallback(
    debounce(updateSuggestions, TYPING_DELAY),
    []
  );

  const onChangeZipCode = useCallback((value: string, country: string) => {
    if (
      AUTOCOMPLETE_VALID_COUNTRIES.includes(country) &&
      value.length >= MIN_ZIP_CODE_LENGTH
    ) {
      debouncedUpdateSuggestions(value, country);
    }
  }, []);

  useEffect(() => {
    props.onChangeSuggestions(suggestions);
  }, [suggestions]);

  return {
    suggestions,
    onChangeZipCode,
  };
};
